/* oxlint-disable jsx-a11y/no-static-element-interactions */
/* oxlint-disable jsx-a11y/mouse-events-have-key-events */
/* oxlint-disable jsx-a11y/aria-props */
/* oxlint-disable jsx-a11y/no-noninteractive-element-interactions */
import classNames from 'classnames';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { SPOILER_TYPE } from './../../ricos-content/index.js';
import type { RichContent } from './../../ricos-schema/legacy/wix/rich_content/v1/index.js';
import { buildThemeCssVars } from './../../ricos-styles/index.js';
import type {
  NodeViewDecorator,
  NodeViewRenderers,
  ViewerContextualData,
} from './../../ricos-types/index.js';
import {
  AccessibilityListener,
  GlobalContext,
  getLangDir,
} from './../../wix-rich-content-common/index.js';

import viewerStyles from '../statics/rich-content-viewer-scss.js';
import rtlStyles from '../statics/rtl-scss.js';
import type { ContentViewerProps } from '../types.js';
import AddAnchors from './components/AddAnchors.js';
import ContentTraverser from './ContentTraverser.js';

const styles = { ...viewerStyles, ...rtlStyles };

const ContentViewer: React.FC<ContentViewerProps> = props => {
  const {
    config = {},
    onHover,
    isMobile = false,
    addAnchors,
    onMouseOver = () => {},
    content,
    setRef,
    typeMappers,
    nodeViewRenderers,
    nodeViewDecorators,
    experiments = {},
    t,
  } = props;

  const [minHeight, setMinHeight] = useState<number | undefined>(undefined);
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  // SAFARI BUG FIX: Safari jumps to wrong scroll position during rerenders
  // ResizeObserver watches inner content and applies min-height to outer viewer container to prevent jumping
  useLayoutEffect(() => {
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    if (!isSafari || !contentRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(entries => {
      const newHeight = entries[0].contentRect.height;
      if (newHeight > 0) {
        setMinHeight(newHeight);
      }
    });

    resizeObserver.observe(contentRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [content]);

  const handleRef = useCallback(
    (node: HTMLDivElement | null) => {
      viewerRef.current = node;
      if (setRef) {
        (setRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [setRef]
  );

  const SpoilerViewerWrapper = config[SPOILER_TYPE]?.SpoilerViewerWrapper;
  const contextualData = getContextualData(props);

  const viewerClassName = classNames(styles.viewer, {
    [rtlStyles.rtl]: contextualData.textDirection === 'rtl',
  });

  const style = buildThemeCssVars(contextualData.theme, content?.documentStyle ?? {});

  const dataId = { 'data-id': 'content-viewer' };

  return (
    <GlobalContext.Provider key={'RicosViewer'} value={{ experiments, isMobile, t }}>
      <div
        className={classNames(styles.wrapper)}
        dir={contextualData.languageDir}
        style={{
          ...style,
          minHeight: minHeight ? `${minHeight}px` : undefined,
        }}
        onMouseEnter={e => onHover && onHover(e)}
        onMouseOver={onMouseOver}
        ref={handleRef}
        {...dataId}
      >
        {props.devTools}
        <div className={viewerClassName} ref={contentRef}>
          <AddAnchors addAnchors={addAnchors}>
            <ContentTraverser
              content={content as RichContent}
              typeMappers={typeMappers}
              context={contextualData}
              nodeViewRenderers={nodeViewRenderers as NodeViewRenderers}
              nodeViewDecorators={nodeViewDecorators as NodeViewDecorator<JSX.Element>[]}
              SpoilerViewerWrapper={SpoilerViewerWrapper}
            />
          </AddAnchors>
        </div>
        <AccessibilityListener isMobile={isMobile} />
      </div>
    </GlobalContext.Provider>
  );
};

const getContextualData = ({
  t,
  theme,
  plugins,
  isMobile = false,
  anchorTarget,
  relValue,
  customAnchorScroll,
  config,
  locale,
  disabled,
  seoMode,
  iframeSandboxDomain,
  textAlignment,
  experiments,
  textDirection,
  direction,
  helpers,
  onNodeClick,
  jsdomMode,
  baseUrl,
  isPrint,
}: ContentViewerProps): ViewerContextualData => {
  return {
    t,
    theme: theme || {},
    isMobile,
    anchorTarget,
    relValue,
    customAnchorScroll,
    config,
    helpers: helpers || {},
    locale: locale || 'en',
    disabled,
    seoMode: seoMode ?? (isPrint ? {} : undefined),
    iframeSandboxDomain,
    disableRightClick: config?.uiSettings?.disableRightClick,
    textAlignment,
    experiments,
    textDirection,
    languageDir: direction || getLangDir(locale),
    onNodeClick,
    jsdomMode,
    baseUrl,
    isPrint,
    plugins,
  };
};

export default ContentViewer;
